import { lazy, Suspense } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, Route, Routes } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";

const lazyNamed = (loader, exportName) =>
  lazy(() => loader().then((module) => ({ default: module[exportName] })));

const AuthPage = lazyNamed(() => import("../pages/AuthPage"), "AuthPage");
const Browse = lazyNamed(() => import("../pages/Browse"), "Browse");
const Checkout = lazyNamed(() => import("../pages/Checkout"), "Checkout");
const ConversationPage = lazyNamed(
  () => import("../components/UserPage/ConversationPage"),
  "ConversationPage"
);
const CreateListingVisual = lazyNamed(
  () => import("../components/CreateListing/CreateListingVisual"),
  "CreateListingVisual"
);
const FavoritesSection = lazyNamed(
  () => import("../components/UserPage/FavoritesSection"),
  "FavoritesSection"
);
const Home = lazyNamed(() => import("../pages/Home"), "Home");
const HowItWorks = lazyNamed(() => import("../pages/Legal/HowItWorks"), "HowItWorks");
const InboxSection = lazyNamed(
  () => import("../components/UserPage/InboxSection"),
  "InboxSection"
);
const Legal = lazyNamed(() => import("../pages/Legal"), "Legal");
const NewConversationPage = lazyNamed(
  () => import("../components/UserPage/NewConversationPage"),
  "NewConversationPage"
);
const OrderConfirmation = lazyNamed(
  () => import("../pages/OrderConfirmation"),
  "OrderConfirmation"
);
const PaymentCancelled = lazyNamed(
  () => import("../pages/PaymentCancelled"),
  "PaymentCancelled"
);
const PaymentSuccess = lazyNamed(() => import("../pages/PaymentSuccess"), "PaymentSuccess");
const PickupConfirmation = lazyNamed(
  () => import("../pages/PickupConfirmation"),
  "PickupConfirmation"
);
const ProductDetail = lazyNamed(() => import("../pages/ProductDetail"), "ProductDetail");
const Products = lazyNamed(() => import("../pages/Products"), "Products");
const Services = lazyNamed(() => import("../pages/Services"), "Services");
const Settings = lazyNamed(() => import("../pages/Settings"), "Settings");
const TicketSearch = lazy(() => import("../pages/Tickets"));
const UserPage = lazyNamed(() => import("../pages/UserPage"), "UserPage");
const UserStorefront = lazyNamed(() => import("../pages/UserStorefront"), "UserStorefront");

function RouteLoadingFallback() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/" element={<Home />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/books" element={<Navigate to="/products?c=books" replace />} />
        <Route path="/products" element={<Products />} />
        <Route path="/services" element={<Services />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/settings" element={<Settings />} />
        <Route
          path="/create-product"
          element={
            <ProtectedRoute>
              <CreateListingVisual />
            </ProtectedRoute>
          }
        />
        <Route path="/myy" element={<CreateListingVisual />} />
        <Route
          path="/user/:tab?"
          element={
            <ProtectedRoute>
              <UserPage />
            </ProtectedRoute>
          }
        />
        <Route path="/nouto/:id" element={<PickupConfirmation />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-status/:orderId" element={<OrderConfirmation />} />
        <Route path="/liput" element={<TicketSearch />} />
        <Route path="/inbox" element={<InboxSection />} />
        <Route path="/inbox/:conversationId" element={<ConversationPage />} />
        <Route path="/inbox/new" element={<NewConversationPage />} />
        <Route path="/favorites" element={<FavoritesSection />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/legal/how-it-works" element={<HowItWorks />} />
        <Route path="/payment-success/:orderId" element={<PaymentSuccess />} />
        <Route path="/payment-cancelled/:orderId" element={<PaymentCancelled />} />
        <Route path="/:userName" element={<UserStorefront />} />
      </Routes>
    </Suspense>
  );
}
